// @ts-nocheck
import { Calendar, CheckCircle, ChevronLeft, ChevronRight, Image as ImageIcon, LogOut, X } from "lucide-react";
import React, { useEffect, useMemo,useState } from "react";
import { useNavigate,useParams } from "react-router-dom";

import ClienteCronograma from "@/features/cliente/pages/ClienteCronograma";
import { getRelatoriosByObra } from "@/features/diario/services/relatoriosService";
import { useRouteRefresh } from "@/features/sistema/hooks/useRouteRefresh";
import { supabase } from "@/features/sistema/services/supabaseClient";

type Obra = {
  id: string;
  nome: string;
  foto_capa: string | null;
  endereco?: string | null;
  contratante?: string | null;
  data_inicio?: string | null;
  data_fim?: string | null;
  status?: "em_andamento" | "paralisada" | "concluida" | null;
  supervisor_nome?: string | null;
  coordenador_nome?: string | null;
  estagiario_nome?: string | null;
};

export default function ClienteHome() {
  const { obraId } = useParams();
  const navigate = useNavigate();
  const [obra, setObra] = useState<Obra | null>(null);
  const [diarios, setDiarios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fotos, setFotos] = useState<any[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [showAllFotos, setShowAllFotos] = useState(false);

  useRouteRefresh(() => {
    if (!obraId) return;
    (async () => {
      setLoading(true);
      const { data: obraData } = await supabase
        .from("obras")
        .select("id, nome, foto_capa, endereco, contratante, data_inicio, data_fim, status, supervisor_nome, coordenador_nome, estagiario_nome")
        .eq("id", obraId)
        .maybeSingle();
      setObra(obraData);

      const rels = await getRelatoriosByObra(obraId);
      setDiarios((rels || []).filter((r: any) => r.status === "aprovado"));

      // 🔎 TODAS as fotos da obra (apenas de relatórios aprovados)
      // Inner join com relatorios: filtra por obra e status aprovado
      const { data: fotosData } = await supabase
        .from("fotos")
        .select("id,url,relatorio_id,criado_em,relatorios!inner(obra_id,status)")
        .eq("relatorios.obra_id", obraId)
        .eq("relatorios.status", "aprovado")
        .order("criado_em", { ascending: false })
        ;
      setFotos(Array.isArray(fotosData) ? fotosData : []);

      setLoading(false);
    })();
  });

  const titulo = useMemo(() => obra?.nome ?? "Obra", [obra]);

  const handleLogout = async () => {
    // ✅ Abordagem mais simples e direta
    try {
      // Limpar dados locais
      localStorage.clear();
      sessionStorage.clear();
      
      // Logout do Supabase
      await supabase.auth.signOut();
      
      // Redirecionamento direto e imediato
      window.location.href = '/ERP-Acor/login';
    } catch (error) {
      console.error('Erro no logout:', error);
      // Força redirecionamento mesmo com erro
      window.location.href = '/ERP-Acor/login';
    }
  };

  if (loading) {
    return <div className="p-4">Carregando…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto p-4 space-y-8">
        {/* HERO / CAPA */}
        <div className="relative w-full h-40 sm:h-52 md:h-64 rounded-2xl overflow-hidden border border-secondary shadow-sm">
          <img
            src={obra?.foto_capa || "/placeholder.jpg"}
            alt={titulo}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
            <div>
              <h1 className="text-white text-2xl sm:text-3xl font-ageo-bold leading-tight">{titulo}</h1>
              {obra?.endereco && (
                <p className="text-white/90 text-xs sm:text-sm line-clamp-1">{obra.endereco}</p>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm font-medium bg-white/90 hover:bg-white text-foreground rounded-md transition-colors shadow-sm"
              title="Sair e voltar para login"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>

        {/* Informações da obra (grid 3x2) */}
        <section className="rounded-2xl border border-secondary bg-white p-6 shadow-sm">
          <h2 className="text-lg font-ageo-bold mb-3 text-foreground">Informações</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg border border-secondary bg-background/60">
              <div className="text-[11px] uppercase tracking-wide opacity-70">Contratante</div>
              <div className="text-sm sm:text-base font-ageo-bold">{obra?.contratante ?? "—"}</div>
            </div>
            <div className="p-3 rounded-lg border border-secondary bg-background/60">
              <div className="text-[11px] uppercase tracking-wide opacity-70">Período</div>
              <div className="text-sm sm:text-base font-ageo-bold">
                {obra?.data_inicio ?? "—"}{obra?.data_fim ? ` → ${obra.data_fim}` : ""}
              </div>
            </div>
            <div className="p-3 rounded-lg border border-secondary bg-background/60">
              <div className="text-[11px] uppercase tracking-wide opacity-70">Status</div>
              <div className="text-sm sm:text-base font-ageo-bold">
                {obra?.status ? (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 border border-green-300 text-green-700">
                    {obra.status === "em_andamento" ? "Em andamento" : obra.status === "paralisada" ? "Paralisada" : "Concluída"}
                  </span>
                ) : "—"}
              </div>
            </div>
            {/* Linha de baixo (Supervisor, Coordenador, Estagiário) */}
            <div className="p-3 rounded-lg border border-secondary bg-background/60">
              <div className="text-[11px] uppercase tracking-wide opacity-70">Supervisor</div>
              <div className="text-sm sm:text-base font-ageo-bold">{obra?.supervisor_nome ?? "—"}</div>
            </div>
            <div className="p-3 rounded-lg border border-secondary bg-background/60">
              <div className="text-[11px] uppercase tracking-wide opacity-70">Coordenador</div>
              <div className="text-sm sm:text-base font-ageo-bold">{obra?.coordenador_nome ?? "—"}</div>
            </div>
            <div className="p-3 rounded-lg border border-secondary bg-background/60">
              <div className="text-[11px] uppercase tracking-wide opacity-70">Estagiário</div>
              <div className="text-sm sm:text-base font-ageo-bold">{obra?.estagiario_nome ?? "—"}</div>
            </div>
          </div>
        </section>

        {/* Diários Aprovados */}
        <section className="rounded-2xl border border-secondary bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-ageo-bold flex items-center gap-2 text-foreground">
              <CheckCircle className="w-5 h-5" /> Diários aprovados
            </h2>
          </div>

          {diarios.length === 0 ? (
            <div className="text-sm text-gray-500">Não há diários aprovados.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {diarios.map((d) => (
                <div
                  key={d.id}
                  onClick={() => navigate(`/cliente/obras/${obraId}/diarios/${d.id}`)}
                  className="rounded-xl p-4 cursor-pointer border border-green-200 bg-green-50 hover:bg-green-100 transition shadow-sm"
                >
                  <div className="font-ageo-bold mb-1 text-foreground">Diário {d.numero}</div>
                  <div className="text-xs flex items-center gap-1 text-gray-600">
                    <Calendar className="w-3 h-3" />
                    {String(d.data).split("T")[0] ?? "—"}
                  </div>
                  {d.comentarios && (
                    <p className="text-xs mt-2 line-clamp-2 opacity-80 text-gray-600">{d.comentarios}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Cronograma (Cliente) */}
        <ClienteCronograma obraId={obraId!} />

        {/* Últimas fotos (limitado a 24 + Ver mais) */}
        <section className="rounded-2xl border border-secondary bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-ageo-bold flex items-center gap-2 text-foreground">
              <ImageIcon className="w-5 h-5" /> Últimas fotos
            </h2>
          </div>
          {fotos.length === 0 ? (
            <div className="text-sm text-gray-500">Sem fotos publicadas ainda.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1">
                {(showAllFotos ? fotos : fotos.slice(0, 24)).map((f:any, idx:number) => (
                  <button
                    key={f.id}
                    className="relative group"
                    onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                    title="Visualizar"
                  >
                    <img
                      src={f.url}
                      alt="Foto da obra"
                      className="w-full h-24 object-cover rounded-lg shadow-sm hover:opacity-90 transition"
                      loading="lazy"
                      draggable={false}
                    />
                  </button>
                ))}
              </div>
              {fotos.length > 24 && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowAllFotos(v => !v)}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 text-foreground text-sm"
                  >
                    {showAllFotos ? "Ver menos" : "Ver mais fotos"}
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {/* Lightbox simples */}
        {lightboxOpen && fotos[lightboxIndex] && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            role="dialog"
            aria-modal="true"
            onWheel={(e) => {
              if (e.deltaY > 0) setLightboxIndex(i => Math.min(fotos.length - 1, i + 1));
              if (e.deltaY < 0) setLightboxIndex(i => Math.max(0, i - 1));
            }}
            onTouchStart={(e) => setTouchStartX(e.changedTouches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchStartX === null) return;
              const dx = e.changedTouches[0].clientX - touchStartX;
              if (dx > 40) setLightboxIndex(i => Math.max(0, i - 1));
              if (dx < -40) setLightboxIndex(i => Math.min(fotos.length - 1, i + 1));
              setTouchStartX(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") setLightboxIndex(i => Math.max(0, i - 1));
              if (e.key === "ArrowRight") setLightboxIndex(i => Math.min(fotos.length - 1, i + 1));
              if (e.key === "Escape") setLightboxOpen(false);
            }}
            tabIndex={-1}
          >
            <button
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => setLightboxOpen(false)}
              title="Fechar"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <button
              className="absolute left-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => setLightboxIndex(i => Math.max(0, i - 1))}
              title="Anterior"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
            <img
              src={fotos[lightboxIndex].url}
              alt="Foto da obra"
              className="max-h-[80vh] max-w-[90vw] rounded-lg shadow-2xl select-none"
              draggable={false}
            />
            <button
              className="absolute right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
              onClick={() => setLightboxIndex(i => Math.min(fotos.length - 1, i + 1))}
              title="Próxima"
              style={{ top: '50%' }}
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
